import { useMemo, useState, useEffect } from 'react';
import {
  AppstoreOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { Button, ConfigProvider, Form, Input, Layout, Menu, Modal, Space, Tag, Typography, message } from 'antd';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import JobsPage from './pages/jobs';
import PapersPage from './pages/papers';
import QueryPage from './pages/query';
import AddCartPage from './pages/addCart';
import HomePage from './pages/home';
import themeConfig from './theme.json';
import { changePassword, getCurrentUser, loginUser, logoutUser } from './services/api';

const { Header, Content } = Layout;
const { Title } = Typography;

function LegacyGraphRedirect() {
  const { graphId } = useParams();
  return <Navigate replace to={`/graph/${encodeURIComponent(graphId)}`} />;
}

function AppShell() {
  const [graphId, setGraphId] = useState(null);
  const [graphTitle, setGraphTitle] = useState('');
  const [graphChatModel, setGraphChatModel] = useState('gpt-6-astra');
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordForm] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();

  const items = useMemo(
    () => [
      { key: '/papers', icon: <ReadOutlined />, label: 'Select Papers' },
      { key: '/index', icon: <AppstoreOutlined />, label: 'Manage Graphs' },
    ],
    []
  );

  useEffect(() => {
    getCurrentUser()
      .then(setUser)
      .catch(() => setUser({ authenticated: false }))
      .finally(() => setAuthLoading(false));
  }, []);

  const handleLogin = async (username, password) => {
    const nextUser = await loginUser(username, password);
    setUser(nextUser);
    message.success(`Welcome, ${nextUser.display_name || nextUser.username}.`);
    navigate('/index');
  };

  const handleLogout = async () => {
    await logoutUser();
    setUser({ authenticated: false });
    navigate('/');
  };

  const handlePublic = async () => {
    if (user?.authenticated) await logoutUser();
    setUser({ authenticated: false });
    navigate('/papers');
  };

  const handleChangePassword = async (values) => {
    setPasswordSubmitting(true);
    try {
      await changePassword(
        values.currentPassword,
        values.newPassword,
        values.confirmPassword
      );
      message.success('Password changed successfully.');
      passwordForm.resetFields();
      setPasswordModalOpen(false);
    } catch (error) {
      message.error(error.message || 'Unable to change password.');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  return (
    <ConfigProvider
      theme={themeConfig}
    >
      <Layout className="app-shell">
        <div className="app-shell__backdrop" />
        <Header className="app-header">
          <div className="app-brand">
            <Title level={3}>DataVault KG</Title>
          </div>
          {location.pathname !== '/' && (
            <>
              <Menu
                mode="horizontal"
                selectedKeys={[location.pathname]}
                items={items}
                onClick={({ key }) => navigate(key)}
                className="app-nav"
              />
              <Space>
                <Tag color={user?.authenticated ? 'purple' : 'default'}>
                  {user?.authenticated ? (user.display_name || user.username) : 'Public'}
                </Tag>
                {user?.authenticated && (
                  <>
                    <Button onClick={() => setPasswordModalOpen(true)}>
                      Change password
                    </Button>
                    <Button onClick={handleLogout}>Logout</Button>
                  </>
                )}
              </Space>
            </>
          )}
        </Header>

        <Content className="app-content">
          <Routes>
            <Route path="/add/:id" element={<AddCartPage />} />
            <Route path="/" element={(
              <HomePage
                user={user}
                loading={authLoading}
                onPublic={handlePublic}
                onLogin={handleLogin}
                onContinue={() => navigate('/index')}
              />
            )} />
            <Route path="/papers" element={<PapersPage user={user} />} />
            <Route path="/index" element={<JobsPage setGraphId={(id, chatModel, title) => { setGraphId(id); setGraphChatModel(chatModel || 'gpt-6-astra'); setGraphTitle(title || ''); }} />} />
            <Route path="/index/:graphId" element={<JobsPage setGraphId={(id, chatModel, title) => { setGraphId(id); setGraphChatModel(chatModel || 'gpt-6-astra'); setGraphTitle(title || ''); }} />} />
            <Route path="/query" element={<QueryPage graphId={graphId} graphTitle={graphTitle} initialChatModel={graphChatModel} />} />
            <Route path="/graph/:graphId" element={<QueryPage />} />
            <Route path="/query/:graphId" element={<LegacyGraphRedirect />} />
          </Routes>
        </Content>
        <Modal
          title="Change password"
          open={passwordModalOpen}
          okText="Update password"
          confirmLoading={passwordSubmitting}
          onOk={() => passwordForm.submit()}
          onCancel={() => {
            passwordForm.resetFields();
            setPasswordModalOpen(false);
          }}
          destroyOnHidden
        >
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handleChangePassword}
          >
            <Form.Item
              name="currentPassword"
              label="Current password"
              rules={[{ required: true, message: 'Enter your current password.' }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Form.Item
              name="newPassword"
              label="New password"
              rules={[
                { required: true, message: 'Enter a new password.' },
                { min: 8, message: 'Use at least 8 characters.' },
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="Confirm new password"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Confirm your new password.' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('New passwords do not match.'));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          </Form>
        </Modal>
      </Layout>
    </ConfigProvider>
  );
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
